/**
 * PageHeader — large serif title, optional eyebrow caption, action slot right.
 * Hairline rule below.
 */
import * as React from 'react';
import { cn } from '../../lib/cn';

export interface PageHeaderProps {
  /** Optional small uppercase caption above the title. */
  eyebrow?: string;
  title: string;
  description?: string;
  /** Right-aligned actions (Button etc.). */
  actions?: React.ReactNode;
  className?: string;
  /**
   * `default` (rule below, compact) for routes with imagery; `editorial`
   * (typography-only, larger title, more whitespace, no border rule) for
   * detail screens with no hero photo per AC-058 / UX §3.6.
   */
  variant?: 'default' | 'editorial';
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  className,
  variant = 'default',
}: PageHeaderProps) {
  const editorial = variant === 'editorial';
  return (
    <header
      className={cn(
        editorial
          ? 'pt-4 pb-16 mb-12'
          : 'pb-8 mb-10 border-b border-ink-200',
        'flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-8',
        className,
      )}
      data-variant={variant}
      data-testid="page-header"
    >
      <div className="min-w-0">
        {eyebrow ? (
          <p
            className={cn(
              'font-sans uppercase tracking-[0.18em] text-ink-500',
              editorial
                ? 'text-[0.7rem] mb-6'
                : 'text-xs mb-3',
            )}
          >
            {eyebrow}
          </p>
        ) : null}
        <h1
          className={cn(
            'font-serif text-ink-900 leading-tight tracking-tight',
            editorial
              ? 'text-5xl md:text-6xl lg:text-7xl'
              : 'text-4xl md:text-5xl',
          )}
        >
          {title}
        </h1>
        {description ? (
          <p
            className={cn(
              'font-sans text-ink-600 leading-relaxed max-w-prose',
              editorial ? 'mt-6 text-lg italic' : 'mt-3 text-base',
            )}
          >
            {description}
          </p>
        ) : null}
      </div>
      {actions ? <div className="shrink-0">{actions}</div> : null}
    </header>
  );
}
